// What a task-pane Sentry event may carry (GID-83).
//
// Every user's errors are reported, the user's content is not. Same rules as
// the backend (backend/sentry_setup.py _redact_content): no request bodies or
// query strings, quoted text in messages masked, console breadcrumbs dropped
// (the pane logs response metadata, which carries plan details), payload
// previews removed. The user is known only by the pseudonymous id the backend
// also uses, so one person's frontend and backend errors line up. Kept apart
// from sentry.js so the rules are unit-tested without the Sentry SDK.

const QUOTED = /'[^']*'|"[^"]*"/g;
const REDACTED_REQUEST_KEYS = ["data", "query_string", "cookies", "headers"];

export function maskQuoted(text) {
  if (typeof text !== "string") return text;
  return text.replace(QUOTED, (match) => `${match[0]}…${match[0]}`);
}

export function keepBreadcrumb(breadcrumb) {
  return (breadcrumb?.category || "") !== "console";
}

export function redactEvent(event) {
  const out = { ...event };
  if (out.request && typeof out.request === "object") {
    const request = { ...out.request };
    REDACTED_REQUEST_KEYS.forEach((key) => delete request[key]);
    out.request = request;
  }
  if ("message" in out) out.message = maskQuoted(out.message);
  if (out.exception?.values) {
    out.exception = {
      ...out.exception,
      values: out.exception.values.map((value) => ({ ...value, value: maskQuoted(value.value) })),
    };
  }
  const crumbs = Array.isArray(out.breadcrumbs) ? out.breadcrumbs : out.breadcrumbs?.values;
  if (Array.isArray(crumbs)) {
    const kept = crumbs.filter(keepBreadcrumb);
    out.breadcrumbs = Array.isArray(out.breadcrumbs) ? kept : { ...out.breadcrumbs, values: kept };
  }
  if (out.extra && typeof out.extra === "object") {
    out.extra = Object.fromEntries(
      Object.entries(out.extra).filter(([key]) => !String(key).endsWith("_preview"))
    );
  }
  out.tags = { ...(out.tags || {}), content: "redacted" };
  return out;
}

export async function pseudonymousUserId(userId) {
  if (!userId) return null;
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) return null;
  const bytes = new TextEncoder().encode(`novatime-sentry:${userId}`);
  const digest = await subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("")
    .slice(0, 16);
}
